/**
 * /admin/rechnungen-dashboard — the one invoice dashboard (Rechnungen 2.0 — R5).
 *
 * ONE SOURCE AT A TIME, ALWAYS NAMED. Vantio's invoices live in two worlds —
 * rows this application created, and mirrored Bexio documents — and the product
 * promise is that a reader always knows which of the two they are looking at.
 * R2 established the source as URL state; everything on this page is scoped to
 * it. There is no combined view, no merged total and no "Alle Quellen", here or
 * on the server: two id spaces, two status vocabularies and two notions of
 * "open" have no honest sum.
 *
 * THIS PAGE ORCHESTRATES AND DOES NOT CALCULATE. The four cards are R4's summary
 * rendered; the rows are R3's page rendered; the filters report changes to the
 * data hook, which owns the dependency matrix. Nothing here folds `items` into a
 * figure — a total taken from the rows would describe ONE PAGE of a filtered
 * population, would change when the reader turned to page two, and would
 * disagree with the card above it. That is the defect the Company-Overview
 * Rechnungen panel had and the reason this route now owns the question.
 *
 * THE CTA FOLLOWS THE PROVIDER, NEVER THE VIEW. An organisation that bills in
 * Bexio may read its Vantio history; that history is a record, not a workspace,
 * and offering a "new Vantio invoice" while it is open would put a button in
 * front of an operator whose backend will refuse the write. `useInvoiceSource`
 * answers "which world am I reading", `useInvoiceCapabilities` answers "where
 * may I create" — two questions, deliberately never derived from one another.
 *
 * A FAILED PART FAILS ALONE. Summary, list and Bexio status are three reads and
 * each states its own failure: a summary that will not load leaves the table
 * standing, a list that will not load leaves the cards standing, and a stale
 * sync hides neither. A page-wide "konnte nicht geladen werden" would throw away
 * two working answers because a third did not arrive.
 *
 * THE EXPORT IS THE POPULATION, NOT THE PAGE (R8). Each workspace's filter row
 * carries its own export button, and each is wired to its own collector: same
 * source, same view, same filters, same sort — every matching row rather than
 * the visible 25. The two collectors are CALLED on every render for the same
 * reason the two data hooks are, and neither asks anything until its button is
 * pressed. The payment one is wired only where the server said `available`: on
 * Vantio there are no individual payments to export, which is not the same thing
 * as an empty list, so no button is offered rather than a disabled one.
 *
 * WHAT IS DELIBERATELY ABSENT. No donut — on the Bexio side a partially paid
 * invoice is counted in BOTH `paid` and `open`, so those counts do not partition
 * and a ring drawn from them would show overlapping slices without saying so. No
 * upcoming-due list (no honest contract for it yet), no payment total anywhere —
 * the mirror is upsert-only and never deletes — and none of the old panel's
 * borrowed vocabulary.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import useInvoiceSource from "./invoices/useInvoiceSource";
import useInvoiceView from "./invoices/useInvoiceView";
import useInvoiceCapabilities from "./invoices/useInvoiceCapabilities";
import useInvoiceDashboardData from "./invoices/useInvoiceDashboardData";
import useInvoicePaymentsData from "./invoices/useInvoicePaymentsData";
import useInvoiceDashboardExport from "./invoices/useInvoiceDashboardExport";
import InvoiceSourceSwitcher from "./invoices/InvoiceSourceSwitcher";
import InvoiceKpiGrid from "./invoices/InvoiceKpiGrid";
import InvoiceFilters from "./invoices/InvoiceFilters";
import InvoiceTable from "./invoices/InvoiceTable";
import PaymentsFilters from "./invoices/PaymentsFilters";
import PaymentsTable from "./invoices/PaymentsTable";
import {
  INVOICE_CARD,
  InvoiceCreationCta,
  InvoiceDataQualityCard,
  InvoiceViewSwitcher,
  invoiceCount,
} from "./invoices/InvoiceDashboardChrome";
import OverviewDataSourceCard from "../../components/admin/companyOverview/OverviewDataSourceCard";
import { fetchBexioStatus } from "../../api/adminData/bexio";

const RECONCILIATION_ROUTE = "/admin/bexio/reconciliation";
const ACTION =
  "inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground no-underline transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const paymentCount = (n) => `${n} ${n === 1 ? "Zahlung" : "Zahlungen"}`;

export default function AdminInvoiceDashboardPage() {
  // ONE capability read for the whole page. `useInvoiceSource` used to perform
  // its own, and this line performed a second — the same GET twice per mount,
  // with two independent copies of the same loading and error state. The
  // resolver is handed the four fields it consumes and nothing else:
  // `nativeCreationEnabled` stays here, because a boolean that is fail-closed
  // to `false` while loading must never be allowed to decide which world the
  // reader is looking at.
  const capabilities = useInvoiceCapabilities();
  const {
    source,
    provider,
    labels,
    setSource,
    isResolving,
    error,
    retry,
    ignoredRequest,
  } = useInvoiceSource({
    loading: capabilities.loading,
    error: capabilities.error,
    provider: capabilities.provider,
    reload: capabilities.reload,
  });
  const { nativeCreationEnabled } = capabilities;

  // Which workspace, independent of which world. Both hooks below are CALLED on
  // every render — Rules of Hooks leave no choice — and `active` is what keeps
  // the one that is not on screen from asking anything.
  const { view, setView, isPaymentsView } = useInvoiceView();

  const data = useInvoiceDashboardData({ source, active: !isPaymentsView });
  const payments = useInvoicePaymentsData({ source, active: isPaymentsView });
  const isBexio = source === "bexio";

  // The mirror's freshness is INFORMATION, never a gate: a stale sync does not
  // make the rows it already holds untrue, and hiding them would be worse.
  const [bexioStatus, setBexioStatus] = useState({ data: null, error: "" });
  const [statusLoading, setStatusLoading] = useState(false);
  useEffect(() => {
    if (source !== "bexio") return undefined;
    let alive = true;
    setStatusLoading(true);
    fetchBexioStatus()
      .then((res) => {
        if (alive) setBexioStatus({ data: res, error: "" });
      })
      .catch((e) => {
        if (alive) {
          setBexioStatus({
            data: null,
            error: e?.message || "bexio-Status konnte nicht geladen werden.",
          });
        }
      })
      .finally(() => {
        if (alive) setStatusLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [source]);

  // THE EXPORT'S FILTERS ARE THE LIST'S FILTERS, projected from the state the
  // filter row is wired to rather than kept a second time. The only field of the
  // list request not visible here is `tenantId`, which neither workspace renders
  // a control for and which this page has never passed; a tenant filter would
  // have to be added here in the same commit that introduces it.
  const invoiceExportParams = useMemo(
    () => ({
      source,
      q: data.debouncedQ || undefined,
      status: data.status || undefined,
      fromDate: data.fromDate,
      toDate: data.toDate,
      sort: data.sort,
      direction: data.direction,
    }),
    [
      source,
      data.debouncedQ,
      data.status,
      data.fromDate,
      data.toDate,
      data.sort,
      data.direction,
    ]
  );

  const paymentsExportParams = useMemo(
    () => ({
      source,
      q: payments.debouncedQ || undefined,
      fromDate: payments.fromDate,
      toDate: payments.toDate,
      sort: payments.sort,
      direction: payments.direction,
    }),
    [
      source,
      payments.debouncedQ,
      payments.fromDate,
      payments.toDate,
      payments.sort,
      payments.direction,
    ]
  );

  // Called on every render, like the two data hooks above and for the same
  // reason; neither issues a request until its button is pressed.
  const invoiceExport = useInvoiceDashboardExport({
    view: "invoices",
    source,
    params: invoiceExportParams,
    total: data.list.total,
  });

  const paymentsExport = useInvoiceDashboardExport({
    view: "payments",
    source,
    params: paymentsExportParams,
    total: payments.list.total,
    availability: payments.list.availability,
  });

  // The server's word, not an inference from the source: this organisation
  // records no individual payment transactions, which is a different answer
  // from "none in this period".
  const paymentsNotRecorded = payments.list.availability === "not_recorded";

  const resultLabel = data.list.total ? invoiceCount(data.list.total) : "";
  const paymentsResultLabel = payments.list.total
    ? paymentCount(payments.list.total)
    : "";

  return (
    <div className="grid content-start gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="m-0 text-[22px] font-bold text-foreground">Rechnungen</h2>
          <p className="mt-[10px] max-w-[60ch] text-[12px] text-muted-foreground">
            Rechnungen aus Bexio und Vantio getrennt verwalten, filtern und prüfen
            — immer eine der beiden Welten, nie beide zusammen.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isBexio ? (
            <Link className={ACTION} to={RECONCILIATION_ROUTE}>
              Zum Zahlungsabgleich
              <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
            </Link>
          ) : null}
          <InvoiceCreationCta
            nativeCreationEnabled={nativeCreationEnabled}
            provider={provider}
          />
        </div>
      </header>

      {/* R9: the label the eye was missing. Both switches were correct for a
          screen reader and identical for everyone else — two pill rows, one
          above the other, and nothing but the words to say which chose the
          world and which the workspace. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.6px] text-muted-foreground">
          Quelle
        </span>
        <InvoiceSourceSwitcher
          provider={provider}
          source={source}
          labels={labels}
          onSelect={setSource}
        />
      </div>

      {/* A link was written for a world this organization does not have. The URL
          is left exactly as the sender wrote it — rewriting it would add a
          history entry nobody asked for — and the mismatch is explained instead. */}
      {ignoredRequest ? (
        <p role="status" className="m-0 text-[12px] text-muted-foreground">
          Diese Organisation erstellt Rechnungen in Vantio. Eine Bexio-Ansicht gibt
          es hier nicht.
        </p>
      ) : null}

      {source ? (
        <div className="grid gap-4">
          {/* BELOW the world switch and INSIDE the resolved branch. Which world
              is the stronger statement, which workspace of it the weaker one —
              and while no world is known, neither workspace can be shown, so
              offering the choice would lead nowhere. The two are otherwise
              orthogonal: every combination is meaningful and neither resets the
              other. */}
          <InvoiceViewSwitcher view={view} onSelect={setView} />

        {/* R9: `2xl`, not `xl`. At 1280 the sidebar took 320px and left the
            invoice table ~734px against the ~830px its eight columns need, so
            the PRIMARY table scrolled sideways on an ordinary 1440px desktop.
            The two-column reading is worth having — it just needs a screen
            wide enough to pay for it. Below that the cards sit under the
            table, which is also what 1024 and 390 already did. */}
        <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="grid min-w-0 content-start gap-4">
            {/* EXACTLY ONE WORKSPACE IS RENDERED. Both hooks above are called
                either way, but only the active one asks anything — and only the
                active one appears here, so there is never a second table, a
                second filter row or a second page of results in the DOM. */}
            {isPaymentsView ? (
              <>
                {/* R9: a source the server says records no individual payments
                    gets no filter bar at all. `not_recorded` is not an empty
                    list — there is no population to narrow — so a search box, a
                    month picker and a reset button over it promise a narrowing
                    that can never return anything. The decision stays the
                    SERVER's: `availability`, never `source === "native"`, which
                    would put a second copy of that rule in the browser. While
                    the answer is still unknown the bar stays, because "we have
                    not asked yet" is a third state and it is not this one. */}
                {paymentsNotRecorded ? null : (
                  <PaymentsFilters
                    q={payments.q}
                    onQChange={payments.setQ}
                    period={payments.period}
                    onPeriodChange={payments.setPeriod}
                    onReset={payments.reset}
                    canReset={payments.isFiltered}
                    resultLabel={paymentsResultLabel}
                    // R9: symmetric with the invoice side. A failed or pending
                    // read leaves the button standing and disabled — "not right
                    // now" — and only a CONFIRMED `not_recorded` removes it,
                    // which the branch above already does by dropping the whole
                    // bar. `canExport` keeps it disabled until the server has
                    // actually said `available`.
                    onExport={paymentsExport.runExport}
                    exporting={paymentsExport.exporting}
                    canExport={paymentsExport.canExport}
                  />
                )}

                {paymentsExport.error ? (
                  <p role="alert" className="m-0 text-[12px] text-destructive">
                    {paymentsExport.error}
                  </p>
                ) : null}

                <PaymentsTable
                  rows={payments.list.items}
                  total={payments.list.total}
                  availability={payments.list.availability}
                  loading={payments.list.loading}
                  error={payments.list.error}
                  filtered={payments.isFiltered}
                  onReset={payments.reset}
                  onRetry={payments.list.reload}
                  sort={payments.sort}
                  direction={payments.direction}
                  onSortChange={payments.toggleSort}
                  offset={payments.offset}
                  limit={payments.limit}
                  onOffsetChange={payments.setOffset}
                  onLimitChange={payments.setLimit}
                  onShowInvoices={() => setView("invoices")}
                />
              </>
            ) : (
              <>
                <InvoiceKpiGrid
                  summary={data.summary.data}
                  loading={data.summary.loading}
                  error={data.summary.error}
                  onRetry={data.summary.reload}
                />

                <InvoiceFilters
                  source={source}
                  q={data.q}
                  onQChange={data.setQ}
                  status={data.status}
                  onStatusChange={data.setStatus}
                  period={data.period}
                  onPeriodChange={data.setPeriod}
                  onReset={data.reset}
                  canReset={data.isFiltered}
                  resultLabel={resultLabel}
                  onExport={invoiceExport.runExport}
                  exporting={invoiceExport.exporting}
                  canExport={invoiceExport.canExport}
                />

                {invoiceExport.error ? (
                  <p role="alert" className="m-0 text-[12px] text-destructive">
                    {invoiceExport.error}
                  </p>
                ) : null}

                <InvoiceTable
                  source={source}
                  rows={data.list.items}
                  total={data.list.total}
                  loading={data.list.loading}
                  error={data.list.error}
                  filtered={data.isFiltered}
                  onReset={data.reset}
                  onRetry={data.list.reload}
                  sort={data.sort}
                  direction={data.direction}
                  onSortChange={data.toggleSort}
                  offset={data.offset}
                  limit={data.limit}
                  onOffsetChange={data.setOffset}
                  onLimitChange={data.setLimit}
                />
              </>
            )}
          </div>

          <aside className="grid content-start gap-4">
            {isBexio ? (
              <OverviewDataSourceCard
                bexioStatus={bexioStatus}
                loading={statusLoading}
              />
            ) : null}
            {/* The exclusions describe the INVOICE population; there is no
                equivalent question for payments, and repeating the card beside
                them would describe a population that is not on screen. The
                Bexio sync card above it stays in both views, because the
                payment mirror depends on that sync even more directly. */}
            {isPaymentsView ? null : (
              <InvoiceDataQualityCard
                summary={data.summary.data}
                loading={data.summary.loading}
                error={data.summary.error}
              />
            )}
          </aside>
        </div>
        </div>
      ) : error ? (
        <div className={INVOICE_CARD}>
          <p role="status" className="m-0 text-sm text-muted-foreground">
            Es konnte nicht ermittelt werden, wo die Rechnungen dieser Organisation
            geführt werden. Es wird deshalb keine Ansicht als die aktuelle
            angezeigt.
          </p>
          <button type="button" className={`${ACTION} mt-4`} onClick={retry}>
            Erneut versuchen
          </button>
        </div>
      ) : (
        <div className={INVOICE_CARD} aria-busy={isResolving ? "true" : undefined}>
          <p className="m-0 text-sm text-muted-foreground">
            Rechnungsquelle wird ermittelt …
          </p>
        </div>
      )}

      {/* Errors reach the reader even when an explicit `?source=native` already
          gave them something to look at: the world is right, its classification
          is not yet known. */}
      {source && error ? (
        <p role="status" className="m-0 text-[12px] text-muted-foreground">
          Einordnung der Rechnungsquelle konnte nicht geladen werden.{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={retry}
          >
            Erneut versuchen
          </button>
        </p>
      ) : null}
    </div>
  );
}
